import { Box, styled } from '@mui/material';
import { useEffect, useRef } from 'react';

import { AircraftsLegend } from './AircraftsLegend';
import { FlightCategoryLegend } from './FlightCategoryLegend';
import { WindSpeedLegend } from './WindSpeedLegend';

const PREFIX = 'TSMED-WidgetWindLegendBar';
const legendBarClasses = {
  root: `${PREFIX}-root`,
  content: `${PREFIX}-content`,
  flightGroup: `${PREFIX}-flightGroup`,
};

const Root = styled(Box)(({ theme: { spacing, palette } }) => ({
  [`&.${legendBarClasses.root}`]: {
    position: 'absolute',
    overflow: 'hidden',
    cursor: 'grab',
    userSelect: 'none',
    zIndex: 1,

    height: spacing(4),
    bottom: spacing(1),
    left: spacing(1),
    right: spacing(1),
    color: palette.grey[700],
    fontFamily: "'JetBrains Mono', monospace",
    whiteSpace: 'nowrap',
  },

  [`.${legendBarClasses.content}`]: {
    display: 'flex',
    height: '100%',
    flexDirection: 'row',
    flex: '0 0 auto',
    scrollSnapAlign: 'start',
    gap: spacing(1),
    letterSpacing: -0.56,
    overflowX: 'auto',
    // scrollBehavior: 'smooth',

    scrollbarWidth: 'none' /* Firefox */,
    msOverflowStyle: 'none' /* IE 10+ */,
    '&::-webkit-scrollbar': {
      display: 'none' /* Chrome, Safari, and Opera */,
    },
    [`.${legendBarClasses.flightGroup}`]: {
      display: 'flex',
      height: '100%',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 1,
      fontSize: '14px',
      ['& > div']: {
        height: '100%',
        background:
          'linear-gradient(0deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.05) 100%), rgba(29, 42, 55, 0.50)',
        backdropFilter: 'blur(15px)',
        padding: spacing(0, 2),
        ['&:first-of-type']: {
          borderRadius: '10px 0 0 10px',
        },
        ['&:last-of-type']: {
          borderRadius: '0px 10px 10px 0px',
        },
      },
    },
  },
}));
export const LegendBar = ({
  legendExtraEnabled,
}: {
  legendExtraEnabled: boolean;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) {
      return;
    }
    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;
    const onMouseDown = (e: MouseEvent) => {
      isDragging = true;
      startX = e.pageX - container.offsetLeft;
      scrollLeft = content.scrollLeft;
    };
    const onMouseUp = () => (isDragging = false);
    const onMouseLeave = () => (isDragging = false);
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) {
        return;
      }
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 1.5;
      content.scrollLeft = scrollLeft - walk;
    };
    container.addEventListener('mousedown', onMouseDown);

    container.addEventListener('mouseup', onMouseUp);
    container.addEventListener('mouseleave', onMouseUp);
    container.addEventListener('mousemove', onMouseMove);
    return () => {
      container.removeEventListener('mousedown', onMouseDown);
      container.removeEventListener('mouseup', onMouseUp);
      container.removeEventListener('mouseleave', onMouseLeave);
      container.removeEventListener('mousemove', onMouseMove);
    };
  }, []);

  return (
    <Root ref={containerRef} className={legendBarClasses.root}>
      <Box ref={contentRef} className={legendBarClasses.content}>
        <WindSpeedLegend />
        {legendExtraEnabled ? (
          <Box className={legendBarClasses.flightGroup}>
            <AircraftsLegend />
            <FlightCategoryLegend />
          </Box>
        ) : null}
      </Box>
    </Root>
  );
};
